import { Platform } from 'react-native';
import { supabase } from '@/lib/supabase';

let isConfigured = false;

/**
 * Lazily loads the native Google Sign-In module.
 *
 * The module is loaded on demand (not at file-eval time) so the web bundle and
 * any client that lacks the compiled native module never crash on import. Real
 * Google Sign-In only works in a native dev/production build that includes the
 * module — calling it elsewhere throws a guarded, caught error.
 */
async function loadGoogleSignin() {
  const mod = await import('@react-native-google-signin/google-signin');
  return mod;
}

export const googleAuthService = {
  /**
   * Configure the Google Sign-In SDK. Call once at app startup (after fonts /
   * core providers). Safe to call on web — it no-ops.
   *
   * Requires EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID (the Web Client ID from the
   * Google Cloud Console / Supabase Google provider). The iOS client ID is read
   * from the native config produced by the Expo config plugin.
   */
  async configure(): Promise<void> {
    if (isConfigured || Platform.OS === 'web') return;

    const webClientId = process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID;
    const iosClientId = process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID;

    if (!webClientId) {
      console.warn('⚠️ [Google] Missing EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID — sign-in disabled');
      return;
    }

    try {
      const { GoogleSignin } = await loadGoogleSignin();
      GoogleSignin.configure({
        webClientId,
        iosClientId,
        // Request the basic profile + email scopes; offlineAccess gives us an
        // ID token we can hand to Supabase.
        offlineAccess: true,
        scopes: ['profile', 'email'],
      });
      isConfigured = true;
      console.log('🔵 [Google] SDK configured');
    } catch (error) {
      console.warn('⚠️ [Google] configure failed (non-fatal):', error instanceof Error ? error.message : String(error));
    }
  },

  /**
   * Trigger the native Google account picker, then exchange the returned ID
   * token for a Supabase session. On success, AuthContext's onAuthStateChange
   * picks up the SIGNED_IN event and routes the user.
   *
   * Throws 'CANCELLED' if the user dismisses the picker (callers should treat
   * this as a no-op, not an error to surface).
   */
  async signIn(): Promise<void> {
    if (Platform.OS === 'web') {
      throw new Error('Google Sign-In is only available in the native app');
    }

    if (!isConfigured) {
      await this.configure();
    }

    const { GoogleSignin, statusCodes } = await loadGoogleSignin();

    try {
      await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
      const response = await GoogleSignin.signIn();

      // v13+ returns { type: 'success' | 'cancelled', data }
      const idToken =
        (response as any)?.data?.idToken ?? (response as any)?.idToken ?? null;

      if ((response as any)?.type === 'cancelled') {
        throw new Error('CANCELLED');
      }

      if (!idToken) {
        throw new Error('No ID token returned from Google');
      }

      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: idToken,
      });

      if (error) {
        throw new Error(error.message);
      }

      console.log('🔵 [Google] Sign-in succeeded — Supabase session established');
    } catch (error: any) {
      if (
        error?.code === statusCodes?.SIGN_IN_CANCELLED ||
        error?.message === 'CANCELLED'
      ) {
        throw new Error('CANCELLED');
      }
      throw error instanceof Error ? error : new Error(String(error));
    }
  },

  /** Sign out of the native Google session (best-effort). */
  async signOut(): Promise<void> {
    if (Platform.OS === 'web') return;
    try {
      const { GoogleSignin } = await loadGoogleSignin();
      await GoogleSignin.signOut();
    } catch {
      // best-effort — ignore
    }
  },
};
